import { forwardRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface FormPhoneProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  validationError?: string;
  required?: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 10) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  } else if (numbers.length === 11) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  }
  return numbers;
};

const FormPhone = forwardRef<HTMLInputElement, FormPhoneProps>(
  ({ className, error, validationError, required, value, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(formatPhoneNumber(value));

    useEffect(() => {
      setInternalValue(formatPhoneNumber(value));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      onChange(e);
    };

    const handleBlur = () => {
      const formattedValue = formatPhoneNumber(internalValue);
      setInternalValue(formattedValue);
      onChange({
        target: { name: props.name || '', value: formattedValue },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const inputClassName = cn(
      'mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200',
      error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
      className
    );

    return (
      <div>
        <input
          ref={ref}
          type="tel"
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          pattern="^\(\d{2}\) \d{4,5}-\d{4}$"
          title="Digite um telefone válido no formato (00) 0000-0000 ou (00) 00000-0000"
          className={inputClassName}
          required={required}
          {...props}
        />
      </div>
    );
  }
);

FormPhone.displayName = 'FormPhone';

export { FormPhone };
